import { format } from 'date-fns'
import { Check, Pencil, X } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { parseError } from '@/lib/errors'
import { parseDateOnly } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { type EditableSalesLeadFields, useUpdateSalesLead } from '@/models/salesLead'

// Module-level so DateInput's React.memo bails out of the lead detail's 2s poll re-renders.
const DATE_INPUT_PROPS = { autoFocus: true }
const DATE_INPUT_PROPS_PENDING = { autoFocus: true, disabled: true }

interface InlineEditFieldProps {
  leadId: string
  field: keyof EditableSalesLeadFields
  value: string | null
  inputType?: 'text' | 'email' | 'tel' | 'date'
  displayValue?: ReactNode
  placeholder?: string
  inputClassName?: string
}

export function InlineEditField({
  leadId,
  field,
  value,
  inputType = 'text',
  displayValue,
  placeholder = 'Add value',
  inputClassName,
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const updateMutation = useUpdateSalesLead()

  useEffect(() => {
    if (isEditing) {
      setDraft(value ?? '')
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isEditing, value])

  const dateValue = useMemo(() => parseDateOnly(draft), [draft])
  const onDateChange = useCallback((date: Date | undefined) => {
    setDraft(date ? format(date, 'yyyy-MM-dd') : '')
  }, [])

  const save = () => {
    const normalized = draft.trim()
    const currentValue = value ?? ''
    if (normalized === currentValue) {
      setIsEditing(false)
      return
    }
    const patchValue: string | null = inputType === 'date' && normalized === '' ? null : normalized
    updateMutation.mutate(
      { id: leadId, [field]: patchValue } as Parameters<typeof updateMutation.mutate>[0],
      {
        onSuccess: () => setIsEditing(false),
        onError: (error) => toast.error(parseError(error)),
      }
    )
  }

  const cancel = () => {
    setDraft(value ?? '')
    setIsEditing(false)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  if (isEditing) {
    return (
      <span className="inline-flex items-center gap-1">
        {inputType === 'date' ? (
          <span className={cn('w-40', inputClassName)}>
            <DateInput
              value={dateValue}
              onChange={onDateChange}
              inputProps={updateMutation.isPending ? DATE_INPUT_PROPS_PENDING : DATE_INPUT_PROPS}
            />
          </span>
        ) : (
          <Input
            ref={inputRef}
            type={inputType}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={updateMutation.isPending}
            className={cn('h-7 px-2 py-0 text-sm', inputClassName)}
          />
        )}
        <Button
          type="button"
          variant="iconOnly"
          className="text-green-600 hover:bg-green-50"
          title="Save"
          onClick={save}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? <Spinner className="size-3" /> : <Check className="size-3" />}
        </Button>
        <Button
          type="button"
          variant="iconOnly"
          className="text-muted-foreground hover:bg-neutral-100"
          title="Cancel"
          onClick={cancel}
          disabled={updateMutation.isPending}
        >
          <X className="size-3" />
        </Button>
      </span>
    )
  }

  const hasValue = value !== null && value !== ''
  const display = hasValue ? (
    (displayValue ?? value)
  ) : (
    <span className="text-muted-foreground italic">{placeholder}</span>
  )

  return (
    <span className="inline-flex items-center gap-1">
      <span>{display}</span>
      <Button
        type="button"
        variant="iconOnly"
        className="text-muted-foreground hover:text-foreground inline-flex size-5 shrink-0 items-center justify-center rounded hover:bg-neutral-100"
        title="Edit"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="size-3" />
      </Button>
    </span>
  )
}
